import { cn } from "@/lib/utils";

type AuthBackgroundVariant = "auth" | "mentor";
type AuthBackgroundLayer = "page" | "handoff";

type AuthBackgroundProps = {
  variant?: AuthBackgroundVariant;
  layer?: AuthBackgroundLayer;
  className?: string;
};

export function AuthBackground({
  variant = "auth",
  layer = "page",
  className,
}: AuthBackgroundProps) {
  const isMentorVariant = variant === "mentor";

  return (
    <div
      aria-hidden="true"
      data-slot={layer === "handoff" ? "auth-handoff-backdrop" : "auth-background"}
      data-auth-background-layer={layer}
      className={cn(
        "pointer-events-none fixed inset-0 overflow-hidden",
        isMentorVariant ? "bg-[#f6f2ea]" : "bg-[var(--auth-sky-bg-mid,#468cae)]",
        layer === "page" ? "-z-10" : "z-0",
        className,
      )}
    >
      <div
        data-slot="auth-background-base"
        className={
          isMentorVariant
            ? "absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.68)_0%,rgba(255,255,255,0.38)_18%,rgba(255,255,255,0.16)_34%,rgba(255,255,255,0.05)_52%,transparent_72%),linear-gradient(180deg,#fbfaf7_0%,#f4efe5_46%,#efe7db_100%)]"
            : "absolute inset-0 bg-[linear-gradient(180deg,var(--auth-sky-bg-top,#4f96be)_0%,var(--auth-sky-bg-mid,#468cae)_46%,var(--auth-sky-bg-bottom,#3d7fa6)_100%)]"
        }
      />
      {isMentorVariant ? (
        <>
          <div
            data-slot="auth-background-warm"
            className="absolute -left-40 top-[-12rem] h-[20rem] w-[20rem] rounded-full opacity-38 animate-[auth-float_18s_ease-in-out_infinite]"
            style={{
              background:
                "radial-gradient(circle, rgba(255,214,171,0.34) 0%, rgba(255,205,156,0.24) 22%, rgba(255,193,133,0.12) 44%, rgba(255,193,133,0.04) 68%, transparent 90%)",
            }}
          />
          <div
            data-slot="auth-background-rose"
            className="absolute right-[-11rem] top-[38%] h-[21rem] w-[21rem] rounded-full opacity-34 animate-[auth-float-reverse_20s_ease-in-out_infinite]"
            style={{
              background:
                "radial-gradient(circle, rgba(255,235,221,0.34) 0%, rgba(248,214,194,0.22) 24%, rgba(244,188,158,0.12) 46%, rgba(244,188,158,0.04) 70%, transparent 90%)",
            }}
          />
          <div
            data-slot="auth-background-vignette"
            className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.34),transparent_38%),linear-gradient(180deg,rgba(255,255,255,0.2),rgba(255,255,255,0))]"
          />
        </>
      ) : (
        <>
          <div
            data-slot="auth-background-warm"
            className="absolute -left-32 top-[-10rem] h-[22rem] w-[22rem] rounded-full opacity-55"
            style={{
              background:
                "radial-gradient(circle, rgba(147, 201, 230, 0.42) 0%, rgba(126, 182, 217, 0.2) 28%, transparent 72%)",
            }}
          />
          <div
            data-slot="auth-background-cool"
            className="absolute -right-24 top-[22%] h-[18rem] w-[18rem] rounded-full opacity-50"
            style={{
              background:
                "radial-gradient(circle, rgba(74, 143, 184, 0.32) 0%, rgba(58, 120, 152, 0.14) 36%, transparent 72%)",
            }}
          />
          <div
            data-slot="auth-background-vignette"
            className="absolute inset-0 bg-[radial-gradient(ellipse_90%_72%_at_50%_40%,rgba(126,182,217,0.24),transparent_66%),linear-gradient(180deg,rgba(255,255,255,0.1),transparent_38%)]"
          />
        </>
      )}
    </div>
  );
}
